import {
  ENVIRONMENT,
  APPLICATION,
  INT_COUNTRY,
  SECONDARY_PRODUCT,
  PRODUCT_DOMAIN,
} from "@/constants";

import type { StepDef, WizardDefinition } from "./wizard-types";

type Version = "default" | "v1";

const cacheMap: Record<Version, Promise<WizardDefinition> | null> = {
  default: null,
  v1: null,
};

/**
 * ---------------------------------------------------------------------
 *    Definition loader (Magnolia + local JSON file fallback)
 * ---------------------------------------------------------------------
 */

export const localDefinition = (): string => {
  if (INT_COUNTRY) return `${INT_COUNTRY}-quote`;
  if (SECONDARY_PRODUCT) return "expat-quote";

  if (APPLICATION === "renewal") return "staysure-renewal";
  if (APPLICATION === "quote") {
    switch (PRODUCT_DOMAIN) {
      case "AVN":
        return "avanti-quote";
      case "STS":
        return "staysure-quote";
      default:
        return "default";
    }
  }
  return "default";
};

const LOCAL_FILE = (version: Version) => {
  if (ENVIRONMENT === "development") {
    if (version && version !== "default")
      return import(
        `./definitions/${localDefinition()}/definition-${version}.json`
      ).then((m) => m.default as unknown as WizardDefinition);

    return import(`./definitions/${localDefinition()}/definition.json`).then(
      (m) => m.default as unknown as WizardDefinition
    );
  }
  if (version && version !== "default")
    return import(`./definition-${version}.json`).then(
      (m) => m.default as unknown as WizardDefinition
    );
  return import("./definition.json").then(
    (m) => m.default as unknown as WizardDefinition
  );
};

/**
 * Returns the wizard definition, using version-based caching.
 */
export async function getWizardDefinition(
  questionOrderVersion: string | null
): Promise<WizardDefinition> {
  const version: Version =
    questionOrderVersion === "variant" ? "v1" : "default";
  cacheMap[version] ??= LOCAL_FILE(version);
  if (cacheMap[version] === null) {
    throw new Error(`Failed to load wizard definition for version: ${version}`);
  }
  return cacheMap[version]!;
}

export const isRoot = (pathname: string[] | undefined) => {
  return pathname === undefined || pathname.length === 0;
};

export const extractFirstPath = (pathname: string[] | undefined) => {
  if (isRoot(pathname)) {
    return "/";
  }
  if (pathname && pathname.length > 0) {
    return pathname[0];
  }
  return "";
};

/** True if the step should be included in the flow. */
export function isStepPublished(step: StepDef): boolean {
  return !!step?.published;
}

export const getPublishedSteps = (steps: StepDef[] = []): StepDef[] => {
  return steps.filter(isStepPublished);
};

/**
 * Returns `true` if the given `stepId`
 *  • has a prev _or_ next reference of its own, **OR**
 *  • is referenced by some other step’s prev/next
 * Otherwise returns `false` ➜   the step is “orphan” (unreachable).
 */
export function isStepLinked(steps: StepDef[], stepId: string): boolean {
  /** 1️ the step must exist at all */
  const current = getPublishedSteps(steps).find((s) => s.id === stepId);
  if (!current) return false;

  /** 2️ outgoing links (`prev` or `next` it points to) */
  if (typeof current.prev === "string" || typeof current.next === "string") {
    return true;
  }

  /** 3️ incoming links (another step points to it) */
  return steps.some(
    (s) =>
      (typeof s.next === "string" && s.next === stepId) ||
      (typeof s.prev === "string" && s.prev === stepId)
  );
}

export const getCurrentStep = (
  pathname: string[] | undefined,
  steps: StepDef[] = []
): StepDef | undefined => {
  const publishedSteps = getPublishedSteps(steps);

  const firstPath = extractFirstPath(pathname);

  return publishedSteps.find((s) => s.id === firstPath);
};
